import type { PostHogConversationsApi } from "types/posthog-conversations";

const POSTHOG_CONVERSATIONS_CONTAINER_ID = "ph-conversations-widget-container";
const POSTHOG_CONVERSATIONS_LAUNCHER_SELECTOR = 'button[aria-label^="Open chat"]';
const DEFAULT_PANEL_OPEN_TIMEOUT_MS = 5_000;
const DEFAULT_PANEL_OPEN_POLL_INTERVAL_MS = 100;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const isPosthogConversationsPanelOpen = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }

  const container = document.getElementById(POSTHOG_CONVERSATIONS_CONTAINER_ID);
  if (!container) {
    return false;
  }

  return container.querySelector(POSTHOG_CONVERSATIONS_LAUNCHER_SELECTOR) === null;
};

export const observePosthogConversationsPanelOpen = (
  onChange: (isOpen: boolean) => void
): (() => void) => {
  if (
    typeof document === "undefined" ||
    typeof MutationObserver === "undefined" ||
    !document.body
  ) {
    return () => undefined;
  }

  let lastIsOpen = isPosthogConversationsPanelOpen();
  onChange(lastIsOpen);

  const observer = new MutationObserver(() => {
    const nextIsOpen = isPosthogConversationsPanelOpen();
    if (nextIsOpen === lastIsOpen) {
      return;
    }

    lastIsOpen = nextIsOpen;
    onChange(nextIsOpen);
  });

  observer.observe(document.body, {
    attributeFilter: ["aria-label"],
    attributes: true,
    childList: true,
    subtree: true,
  });

  return () => observer.disconnect();
};

export const clickPosthogConversationsLauncherIfPresent = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }

  const container = document.getElementById(POSTHOG_CONVERSATIONS_CONTAINER_ID);
  const launcher = container?.querySelector<HTMLButtonElement>(
    POSTHOG_CONVERSATIONS_LAUNCHER_SELECTOR
  );

  if (!launcher) {
    return false;
  }

  launcher.click();
  return true;
};

export const waitForPosthogConversationsWidgetVisible = async (
  conversations: PostHogConversationsApi,
  maxWaitMs = DEFAULT_PANEL_OPEN_TIMEOUT_MS,
  pollIntervalMs = DEFAULT_PANEL_OPEN_POLL_INTERVAL_MS
): Promise<boolean> => {
  const deadline = Date.now() + maxWaitMs;

  while (Date.now() < deadline) {
    if (conversations.isVisible()) {
      return true;
    }

    await sleep(pollIntervalMs);
  }

  return conversations.isVisible();
};

export const waitForPosthogConversationsPanelOpen = async (
  maxWaitMs = DEFAULT_PANEL_OPEN_TIMEOUT_MS,
  pollIntervalMs = DEFAULT_PANEL_OPEN_POLL_INTERVAL_MS
): Promise<boolean> => {
  const deadline = Date.now() + maxWaitMs;

  while (Date.now() < deadline) {
    if (isPosthogConversationsPanelOpen()) {
      return true;
    }

    await sleep(pollIntervalMs);
  }

  return isPosthogConversationsPanelOpen();
};
